import * as z from "zod/mini";

export const OIDC_BRIDGE_PATH = "/oidc-bridge";

/**
 * state は auth-op へ返す POST 先のパスに入る。`/` や `..` が混ざると
 * 送り先が変わるため、URL に組み立てる前に形を確かめる。
 * oidc-provider の uid は nanoid で、backend のスキーマ上限が128
 */
export const STATE_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/**
 * auth-op へコードを返す POST 先を組み立てる
 *
 * 文字列連結だと、origin の末尾スラッシュで `//oidc/...` になったり、
 * 空文字で相対 URL になって accounts 自身へコードを送ってしまう。
 * 使えない origin なら undefined を返す
 */
export const toInteractionUrl = (
  authOpOrigin: string | undefined,
  state: string
): string | undefined => {
  let url: URL;
  try {
    url = new URL(
      `/oidc/interaction/${encodeURIComponent(state)}`,
      authOpOrigin
    );
  } catch {
    return undefined;
  }

  // 設定を誤ったときにコードを平文で送らない。ローカルの dev server だけ http
  const allowed =
    url.protocol === "https:" ||
    (url.protocol === "http:" && url.hostname === "localhost");

  return allowed ? url.toString() : undefined;
};

/** ログインを挟んだ後に橋渡しページへ戻すための URL */
export const toBridgeUrl = (state: string): string =>
  `${window.location.origin}${OIDC_BRIDGE_PATH}?state=${encodeURIComponent(state)}`;

/** テストが壊れた値を仕込むためだけに export している */
export const STORAGE_KEY = "spwn.oidcBridge.state";

/** 退避を活かす時間。ログインを挟んで戻るには十分で、放置された値は無視できる長さ */
const KEEP_MS = 10 * 60 * 1000;

/** 書いた後に書き換えられうるので、読み出し時も state の形を確かめる */
const storedSchema = z.object({
  state: z.string().check(z.regex(STATE_PATTERN)),
  savedAt: z.number(),
});

/**
 * ログイン画面へ送る前に state を退避する
 *
 * d アカウントのようにフルページ遷移するプロバイダは、完了後にオリジンの
 * ルートへ戻すため `return_url` が効かない。戻り先はプロバイダ側で正規化され、
 * SPWN からは制御できない。
 */
export const keepState = (state: string): void => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ state, savedAt: Date.now() })
    );
  } catch {
    // プライベートモードなどで使えないことがある。return_url だけに頼る
  }
};

export const forgetState = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // 消せなくても古いものは takeKeptState が無視する
  }
};

/** 一度読んだら消す。残しておくと後日の通常のログインで橋渡しへ引き戻してしまう */
export const takeKeptState = (): string | undefined => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
  forgetState();

  if (raw === null) return undefined;

  try {
    const parsed = storedSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return undefined;
    if (Date.now() - parsed.data.savedAt > KEEP_MS) return undefined;

    return parsed.data.state;
  } catch {
    return undefined;
  }
};
